// 10章 石工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_METHODS = `
<svg viewBox="0 0 660 260" role="img" aria-label="石工事の3工法。湿式工法は引金物とだぼで留め裏込めモルタルを充填する。空積工法は内壁で受金物で受ける。乾式工法はファスナーとだぼで留めモルタルを使わず地震に追従する">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20"  y="40" width="200" height="200" rx="10"/>
    <rect x="230" y="40" width="200" height="200" rx="10"/>
    <rect x="440" y="40" width="200" height="200" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13.5" font-weight="bold" text-anchor="middle">
    <text x="120" y="68">外壁湿式工法</text>
    <text x="330" y="68">内壁空積工法</text>
    <text x="540" y="68">外壁乾式工法</text>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="36" y="98">・引金物＋だぼで留付け</text>
    <text x="36" y="120">・裏込めモルタルを充填</text>
    <text x="36" y="142">・石厚25mm以上</text>
    <text x="36" y="164">・1階の腰壁・根石等</text>
    <text x="36" y="200" opacity="0.75">モルタルで一体化する</text>
    <text x="246" y="98">・受金物で受ける</text>
    <text x="246" y="120">・石厚20mm以上</text>
    <text x="246" y="142">・高さ4ｍ以下の内壁</text>
    <text x="246" y="164">・裏込めは幅木裏等のみ</text>
    <text x="246" y="200" opacity="0.75">内壁専用の工法</text>
    <text x="456" y="98">・ファスナー＋だぼ</text>
    <text x="456" y="120">・石厚30mm以上</text>
    <text x="456" y="142">・高さ31ｍ以下の外壁</text>
    <text x="456" y="164">・モルタルを使わない</text>
    <text x="456" y="200" opacity="0.75">地震・変位に追従できる</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="20" y="30">石材1枚：面積0.8㎡以下・幅高さ1,200mm以下・重量70kg以下（10.2.1）</text>
  </g>
</svg>`;

const SVG_WET = `
<svg viewBox="0 0 660 280" role="img" aria-label="外壁湿式工法の断面。コンクリート躯体と石材の間40mm程度をあけ、あと施工アンカーに横筋を溶接した下地に引金物で石を緊結し、だぼで上下段を連結する。裏込めモルタルを1段ごとに充填する">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="40" width="40" height="210"/></g>
  <g fill="currentColor" opacity="0.55">
    <rect x="200" y="50" width="24" height="90"/>
    <rect x="200" y="150" width="24" height="90"/>
  </g>
  <g fill="currentColor" opacity="0.25"><rect x="100" y="40" width="100" height="210"/></g>
  <g stroke="currentColor" stroke-width="2.5">
    <line x1="110" y1="95" x2="200" y2="95"/>
    <line x1="110" y1="195" x2="200" y2="195"/>
  </g>
  <g fill="currentColor">
    <circle cx="212" cy="140" r="5"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="60" y1="265" x2="100" y2="265"/>
    <line x1="60" y1="257" x2="60" y2="273"/>
    <line x1="100" y1="257" x2="100" y2="273"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="55" y="30" opacity="0.85">躯体</text>
    <text x="130" y="35" opacity="0.85">裏込めモルタル</text>
    <text x="205" y="35" opacity="0.85">石材</text>
    <text x="60" y="290" font-weight="bold">取付け代 40mm程度</text>
    <text x="250" y="90">引金物（石を下地に緊結）</text>
    <text x="250" y="145">だぼ（上下段を連結）</text>
    <text x="250" y="185">あと施工アンカー＋横筋</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="330" y="240">裏込めモルタルは1段ごとに2〜3回に分けて充填。</text>
    <text x="330" y="260">上端は石の上端から30〜40mm下げる（10.3.3）</text>
  </g>
</svg>`;

const SVG_DRY = `
<svg viewBox="0 0 660 310" role="img" aria-label="外壁乾式工法。躯体にあと施工アンカーで一次ファスナーを付け、二次ファスナーを介してだぼで石材を留める。スライド方式やロッキング方式で地震の変位に追従し、取付け代は70mm程度、モルタルは使わない">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="40" width="40" height="210"/></g>
  <g fill="currentColor" opacity="0.55"><rect x="240" y="50" width="24" height="190"/></g>
  <g stroke="currentColor" stroke-width="3" fill="none">
    <path d="M100 130 h60 v-20 h20"/>
    <path d="M180 110 h40 v40"/>
  </g>
  <g fill="currentColor">
    <circle cx="230" cy="150" r="5"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="60" y1="268" x2="240" y2="268"/>
    <line x1="60" y1="260" x2="60" y2="276"/>
    <line x1="240" y1="260" x2="240" y2="276"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="55" y="30" opacity="0.85">躯体</text>
    <text x="230" y="35" opacity="0.85">石材</text>
    <text x="110" y="90">一次ファスナー（躯体側）</text>
    <text x="240" y="105">二次ファスナー（石側）</text>
    <text x="250" y="155">だぼ</text>
    <text x="80" y="298" font-weight="bold">取付け代 70mm程度・モルタルなし</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="330" y="220">スライド方式／ロッキング方式で</text>
    <text x="330" y="240">地震の変位に追従（ALCと同じ発想）。</text>
    <text x="330" y="260">目地はシーリング（幅8mm以上）</text>
  </g>
</svg>`;

const ch10: Record<string, Commentary> = {
  "10.1.1": {
    plainSummary:
      "この章は現場打ちコンクリートの表面に天然石・テラゾ（石材）を取り付ける工事のルール。1章とセットで適用する。",
    why: "石は重く割れやすいため、「どうやって落とさずに固定するか」が石工事の中心テーマです。工法は湿式・空積・乾式の3つで、この違いを理解するのが最初のステップです。",
    diagrams: [
      {
        svg: SVG_METHODS,
        caption: "石工事の3工法（10章の全体像）",
      },
    ],
  },

  "10.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②石材の仕上り面が所定の形状・寸法 ③石材の下地への取付けが所要の状態であること。",
  },

  "10.1.3": {
    plainSummary:
      "石材の割付けは特記による。水平打継ぎ部や異種下地の取合い部は原則1枚の石をまたがない。合端のみ込み部は15mm以上を見え掛りと同程度に仕上げる。金物用の穴あけ・道切りは据付け前に加工。下地面の精度は工法ごとに表10.1.1による。",
    why: "石は1枚が高価で加工に手間がかかるため、据え付けてから穴をあけるのは失敗のもと。「加工は工場・据付けは現場」の分担が原則です。下地の1枚またぎを避けるのは、下地が動く境目で石が割れるのを防ぐためです。",
    glossary: [
      {
        term: "合端（あいば）",
        meaning: "隣り合う石材どうしが接する目地の側面。",
      },
      {
        term: "のみ込み",
        meaning: "入隅などで石が下地や他材に隠れて差し込まれる部分。",
      },
    ],
  },

  "10.1.4": {
    plainSummary:
      "モルタルが急激乾燥・凍結のおそれがあれば養生（15.1.4）。床の敷石後はモルタル硬化まで歩行しない。外壁は雨雪養生のシート覆い、仕上げ面はポリエチレンシート等で汚れ防止、出隅は養生材で保護する。",
    points: [
      "据えたばかりの床石は歩かない（モルタルが動くと目地・不陸が狂う）",
      "出隅・角は最も欠けやすい。養生材を必ず付ける",
    ],
  },

  "10.1.5": {
    plainSummary:
      "取付け後は適切な時期に汚れ・モルタルを除去する。石材面の清掃に酸類は使わない（花こう岩でやむを得ず使う場合は金物養生・清水注ぎ・十分な水洗い）。屋内の本磨きは乾いた布で清掃。",
    why: "酸は石の艶を傷め、大理石は溶けます。特に金属を含む石や周囲の金物は酸で変色・腐食するため、原則酸洗いは禁止。花こう岩の例外も厳重な養生と水洗いが条件です。",
  },

  "10.2.1": {
    plainSummary:
      "天然石は矩形で、1枚が面積0.8㎡以下・幅高さ1,200mm以下・重量70kg以下。表面仕上げは粗面仕上げ（のみ切り・びしゃん・ジェットバーナー等）と磨き仕上げ（粗磨き・水磨き・本磨き）から特記で選ぶ。テラゾはJIS A 5411による。",
    why: "1枚の大きさ・重さの上限は、施工の安全と落下防止のためです。仕上げの種類は見た目と滑りにくさを左右します。屋外床は滑りにくい粗面仕上げ、内装壁は美しい本磨き、というように使い分けられます。",
    glossary: [
      {
        term: "ジェットバーナー仕上げ",
        meaning:
          "花こう岩をバーナーで加熱し表面をはじけさせた粗面仕上げ。滑りにくく屋外床に多い。",
      },
      {
        term: "本磨き",
        meaning: "最も細かい砥石とバフで艶出しした鏡面仕上げ。",
      },
      {
        term: "テラゾ",
        meaning: "種石（大理石粒等）をセメント等で固めて磨いた人造石。",
      },
    ],
  },

  "10.2.2": {
    plainSummary:
      "取付け金物。湿式・空積用の引金物・だぼ・かすがいはステンレス（SUS304）製で寸法は石厚による（石厚40mm以上は径4.0mm等）。乾式用のファスナー・だぼは特記（スライド方式・ロッキング方式）。アンカーは湿式・空積はSS400、乾式・特殊部位はSUS304。",
    why: "石を留める金物が錆びると石が落下します。だから外部や水掛りの金物はステンレスが基本。乾式工法のファスナーは、地震で建物が変形しても石が追従して割れないよう、スライドやロッキングの機構を持っています。",
    glossary: [
      {
        term: "引金物",
        meaning: "石材を下地（横筋等）に緊結する線状の金物。",
      },
      {
        term: "だぼ",
        meaning: "上下の石材の合端に差し込んで連結する短いピン。",
      },
      {
        term: "ファスナー",
        meaning:
          "乾式工法で石材を躯体に取り付ける金物。躯体側の一次ファスナーと石側の二次ファスナーで構成。",
      },
    ],
  },

  "10.2.3": {
    plainSummary:
      "セメントモルタルの調合（容積比）は裏込め1:3、敷モルタル1:4、張付け用ペースト1:0、目地モルタル1:0.5。白色系大理石は白セメント＋寒水石粒。浸透性吸水防止剤・石裏面処理材・裏打ち処理材・シーリング材等は特記または専門工事業者の指定品。",
    glossary: [
      {
        term: "裏込めモルタル",
        meaning: "石材と躯体の隙間に充填して石を固定するモルタル。",
      },
      {
        term: "裏打ち処理",
        meaning:
          "石材裏面に樹脂とネットを張り、割れても破片が落ちにくくする処理。",
      },
    ],
  },

  "10.3.1": {
    plainSummary:
      "この節は石厚70mm以下の石材を湿式工法で1階の腰壁・根石部分等に取り付ける工事に適用する。",
  },

  "10.3.2": {
    plainSummary:
      "石材の厚さは25mm以上。引金物用の穴は上端の横目地合端に2か所（両端から100mm程度）、だぼ用の穴は2か所（両端から150mm程度）。",
  },

  "10.3.3": {
    plainSummary:
      "取付け代（石裏〜コンクリート面）は40mm程度。下地は特記がなければあと施工アンカー・横筋流し工法。最下部の石は正確に据え下端をモルタルで固定、上端は引金物で緊結。一般部はだぼに合わせてスペーサーで目地幅を確保。裏込めモルタルは1段ごとに2〜3回に分けて密実に充填（上端は石上端から30〜40mm下げる）。目地幅は6〜10mm。",
    why: "湿式工法の要点は「引金物＋だぼ＋裏込めモルタル」の3点セット。引金物とだぼで石を仮に留め、裏込めモルタルで一体化します。裏込めを一度に詰めると石が押し出される（はらむ）ため、段階的に充填します。上端を下げるのは次の段のだぼと裏込めの取合いのためです。",
    points: [
      "裏込めは一気に詰めない（石が前に押し出される）",
      "だぼ穴・引金物の充填材で金物を固定（がたつき防止）",
      "伸縮調整目地は下地まで達する発泡材＋シーリング",
    ],
    diagrams: [
      {
        svg: SVG_WET,
        caption: "外壁湿式工法の取付け（10.3.3）",
      },
    ],
  },

  "10.4.1": {
    plainSummary:
      "この節は石厚70mm以下の石材を空積工法で高さ4ｍ以下の内壁に取り付ける工事に適用する。",
    glossary: [
      {
        term: "空積工法",
        meaning:
          "裏込めモルタルを全面に充填せず、金物と部分的なモルタルで石を保持する内壁用の工法。",
      },
    ],
  },

  "10.4.2": {
    plainSummary:
      "石材の厚さは20mm以上。加工は湿式（10.3.2）による。受金物用の座掘りは上端の横目地合端に設ける。",
  },

  "10.4.3": {
    plainSummary:
      "下地はあと施工アンカー・横筋流し工法。受金物は最下段から2段目の横目地位置に設ける（積上げ3ｍ以下なら省略可）。引金物の緊結部は石裏と下地の間に50×100mm程度のモルタルで被覆。床から1.8ｍまでの石には補強充填。裏込めは幅木裏に全面（幅木がなければ最下部石の裏面高さ100mm程度まで）。",
    why: "空積工法は内壁専用で、全面裏込めをしない分軽く施工も速い反面、金物での保持が命です。手が届く高さ（1.8ｍまで）を補強するのは、人がぶつかっても落ちないようにするためです。",
    glossary: [
      {
        term: "受金物",
        meaning: "石材の重量を下から受け止める金物。",
      },
      {
        term: "ねむり目地",
        meaning: "目地幅をほとんど取らずに石を突き付ける目地。",
      },
    ],
  },

  "10.5.1": {
    plainSummary:
      "この節は石厚70mm以下の石材を乾式工法で高さ31ｍ以下の建築物の外壁に取り付ける工事に適用する。",
    why: "乾式工法はモルタルを使わずファスナーで留める現代の主流工法です。高さ31ｍ以下という制限は、それを超える超高層は個別の耐風・耐震検討が必要になるためです。",
  },

  "10.5.2": {
    plainSummary:
      "石材の厚さは30mm以上。だぼ用穴は横目地合端に2か所、端あき寸法は石厚の3倍以上、だぼ穴は石厚方向の中央。裏打ち処理の適用は特記。",
  },

  "10.5.3": {
    plainSummary:
      "風圧力対応工法は特記。取付け代（石裏〜コンクリート面）は70mm程度。下地はあと施工アンカー。一次ファスナー→二次ファスナー→だぼで石材を水平垂直に取り付ける。目地幅は8mm以上でシーリング充填（目地寸法幅深さ8mm以上）。",
    why: "乾式工法は湿式より取付け代（70mm）が大きく、その空間でファスナーが石を保持しつつ変位を吸収します。ALCや押出成形セメント板と同じ「動ける外装」の考え方です。目地は必ずシーリングで、湿式のモルタル目地とは違います。",
    points: [
      "一次→二次ファスナー→だぼの順で組む",
      "だぼの充填材固定でがたつきと異音を防ぐ",
      "モルタルを使わないので目地は全てシーリング",
    ],
    diagrams: [
      {
        svg: SVG_DRY,
        caption: "外壁乾式工法のファスナー（10.5.3）",
      },
    ],
  },

  "10.6.1": {
    plainSummary: "この節は石材を床及び階段に取り付ける工事に適用する。",
  },

  "10.6.2": {
    plainSummary:
      "床の石張り。取付け代は石厚50mm以下で35mm程度、50mm超で60mm程度。下地清掃・水湿し後に敷モルタルを均し、仮据え→石を外して張付け用ペーストを塗布→再据えしてゴムハンマーで圧着し本据え。目地幅は屋外4mm以上・屋内3〜6mm。伸縮調整目地は床面積30㎡程度ごと・通路は6ｍ程度ごと。",
    why: "床石は「仮据えして目地通りを確認→一度外してペーストを塗る→本据え」という二段構えが特徴。いきなり本据えすると目地が揃わず不陸も出ます。伸縮調整目地は、床の温度変化による膨張で石が突き上がる（せり上がる）のを防ぎます。",
    diagrams: [],
  },

  "10.6.3": {
    plainSummary:
      "階段の石張り。け上げ石（蹴込み）の取付け代・下地・取付けは湿式による。裏込めは目地部を目止めして踏面コンクリート面まで充填。踏み石の据付けは床の石張り（10.6.2）による。",
    glossary: [
      {
        term: "け上げ石（蹴込み石）",
        meaning: "階段の垂直面（蹴込み部分）に張る石。",
      },
    ],
  },

  "10.7.1": {
    plainSummary:
      "この節は石材を笠木・甲板等に取り付ける場合、隔て板等に使用する場合に適用する。",
  },

  "10.7.2": {
    plainSummary:
      "笠木・甲板等の石張り。工法は湿式または乾式で特記。湿式は笠木の長さ900mm程度とし、片側にだぼ・他端は引金物で留め裏込めモルタルで固定。あと施工アンカー・横筋流し工法による。",
    glossary: [
      {
        term: "笠木",
        meaning: "パラペットや手すり壁の天端を覆う仕上げ材。",
      },
      {
        term: "甲板（こういた）",
        meaning: "カウンターやテーブル状の水平な天板。",
      },
    ],
  },

  "10.7.3": {
    plainSummary:
      "隔て板（トイレ等の仕切り石）は厚さ40mm（特記がなければ）。取付け用モルタルで壁・床に固定し、隔て板どうしは上端をかすがい、合端を接着剤と上下2か所のだぼで堅固に取り付ける。",
    glossary: [
      {
        term: "隔て板",
        meaning: "小便器の間などに立てる石の仕切り板。",
      },
    ],
  },
};

export default ch10;
